import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./Registration.css";
import "./RegistrationForm.css";
import logo from "../../assets/logo.webp";
import { assetUrl } from "../../utils/useContent";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5001";

export default function RegistrationForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTypeId, setActiveTypeId] = useState(null);
  const [valuesByType, setValuesByType] = useState({});
  const [otherByType, setOtherByType] = useState({}); // per-type map: fieldName → other text
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Scroll to top whenever we enter the success (or closed/not-found) state so
  // the user isn't left staring at empty space below the submit button.
  useEffect(() => {
    if (submitted || notFound) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [submitted, notFound]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/registration/forms/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) {
          if (!cancelled) setNotFound(true);
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((data) => {
        if (cancelled || !data) return;
        setForm(data);
        const types = data.types || [];
        setActiveTypeId(types[0]?.id || null);
        // Seed empty values for each type
        const seed = {};
        const otherSeed = {};
        for (const t of types) {
          seed[t.id] = {};
          otherSeed[t.id] = {};
          for (const f of t.fields || []) {
            if (f.type === "checkbox") seed[t.id][f.name] = false;
            else if (f.type !== "heading" && f.type !== "paragraph") seed[t.id][f.name] = "";
          }
        }
        setValuesByType(seed);
        setOtherByType(otherSeed);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  const activeType = useMemo(
    () => (form?.types || []).find((t) => t.id === activeTypeId) || null,
    [form, activeTypeId]
  );

  const setField = (name, val) =>
    setValuesByType((prev) => ({
      ...prev,
      [activeTypeId]: { ...(prev[activeTypeId] || {}), [name]: val },
    }));

  const setOther = (name, val) =>
    setOtherByType((prev) => ({
      ...prev,
      [activeTypeId]: { ...(prev[activeTypeId] || {}), [name]: val },
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!activeType) return;

    // Merge Other text into the submitted value when Other selected
    const values = { ...(valuesByType[activeTypeId] || {}) };
    for (const f of activeType.fields || []) {
      if (f.type === "select" && f.allow_other && values[f.name] === "__other__") {
        const custom = ((otherByType[activeTypeId] || {})[f.name] || "").trim();
        if (!custom && f.required) {
          setError(`Please fill in "Other" for ${f.label}.`);
          return;
        }
        values[f.name] = custom;
      }
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/registration/forms/${encodeURIComponent(slug)}/submissions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type_id: activeTypeId, data: values }),
        }
      );
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const err = payload?.error || `HTTP ${res.status}`;
        if (err === "registration_closed") setError("Registration is closed.");
        else if (err.endsWith("_required")) setError(`Missing required field: ${err.replace("_required", "")}`);
        else if (err.endsWith("_invalid_email")) setError("Please enter a valid email address.");
        else setError("Submission failed. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="rf-root"><div className="container rf-container"><div className="rf-loading">Loading form…</div></div></main>;
  }

  if (notFound || !form) {
    return (
      <>
        <SEO title="Form not found | ASX" description="This registration form doesn't exist or has been removed." noindex />
        <main className="rf-root rf-root-centered">
          <div className="container rf-container">
            <div className="rf-state-card">
              <div className="rf-state-icon rf-state-icon-neutral" aria-hidden>
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
              </div>
              <div className="rf-state-title">Form not found</div>
              <div className="rf-state-sub">This registration form doesn't exist or has been removed.</div>
              <Link to="/register" className="reg-btn reg-btn-primary">Back to registrations</Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!form.is_reg_open) {
    return (
      <>
        <main className="rf-root rf-root-centered">
          <div className="container rf-container">
            <div className="rf-state-card">
              <div className="rf-state-icon rf-state-icon-neutral" aria-hidden>
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              </div>
              <div className="rf-state-title">Registration is closed</div>
              <div className="rf-state-sub">
                <strong>{form.title}</strong> is no longer accepting submissions.
              </div>
              <Link to="/register" className="reg-btn reg-btn-primary">Back to registrations</Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <main className="rf-root rf-root-centered">
          <div className="container rf-container">
            <div className="rf-state-card">
              <div className="rf-state-icon rf-state-icon-success" aria-hidden>
                <svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </div>
              <div className="rf-state-title">You're registered.</div>
              <div className="rf-state-sub">
                Thanks for signing up for <strong>{form.title}</strong>. We'll be in touch soon.
              </div>
              <Link to="/register" className="reg-btn reg-btn-primary">Back to registrations</Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const types = form.types || [];
  const showPicker = types.length > 1;
  const values = valuesByType[activeTypeId] || {};
  const others = otherByType[activeTypeId] || {};

  return (
    <>
      <SEO
        title={`Register — ${form.title} | ASX Montreal`}
        description={form.description || `Sign up for ${form.title} — an ASX Montreal 7v7 soccer event.`}
        image={form.banner_image_path ? assetUrl(form.banner_image_path) : undefined}
      />
      <main className="rf-root">
        <div className="container rf-container">
          <RegistrationHeader form={form} />

          {showPicker && (
            <div className="rf-type-picker">
              <div className="rf-type-picker-label">Registration type</div>
              <div className="rf-type-picker-buttons">
                {types.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`rf-type-btn ${activeTypeId === t.id ? "active" : ""}`}
                    onClick={() => setActiveTypeId(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form className="rf-form" onSubmit={submit} noValidate>
            {(activeType?.fields || []).map((f, idx) => (
              <FieldRenderer
                key={`${activeTypeId}-${idx}-${f.name || f.type}`}
                field={f}
                value={values[f.name]}
                otherValue={others[f.name] || ""}
                onChange={(v) => setField(f.name, v)}
                onOtherChange={(v) => setOther(f.name, v)}
              />
            ))}

            {error && <div className="rf-error">{error}</div>}

            <button type="submit" className="reg-btn reg-btn-primary rf-submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit registration"}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}

function RegistrationHeader({ form }) {
  return (
    <header className="rf-header">
      <Link to="/register" className="rf-back">← Back</Link>
      <div className={`rf-hero ${form.banner_image_path ? "rf-hero-with-banner" : ""}`}>
        {form.banner_image_path && (
          <div className="rf-banner">
            <img src={assetUrl(form.banner_image_path)} alt="" />
          </div>
        )}
        <div className="rf-hero-text">
          {form.league_label && <div className="rf-league">{form.league_label}</div>}
          <h1 className="rf-title">{form.title}</h1>
          {form.description && (
            <p className="rf-desc">{form.description}</p>
          )}
        </div>
      </div>
    </header>
  );
}

function FieldRenderer({ field, value, otherValue, onChange, onOtherChange }) {
  const { name, label, type, required, placeholder, options, allow_other } = field;

  // Display-only blocks
  if (type === "heading") {
    return <h2 className="rf-heading">{label}</h2>;
  }
  if (type === "paragraph") {
    return <p className="rf-paragraph">{label}</p>;
  }

  const id = `f-${name}`;

  if (type === "textarea") {
    return (
      <label className="rf-field" htmlFor={id}>
        <span className="rf-label">{label}{required && <span className="rf-req">*</span>}</span>
        <textarea id={id} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} required={required} />
      </label>
    );
  }

  if (type === "select") {
    const isOther = value === "__other__";
    return (
      <label className="rf-field" htmlFor={id}>
        <span className="rf-label">{label}{required && <span className="rf-req">*</span>}</span>
        <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required}>
          <option value="" disabled>Choose an option…</option>
          {(options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
          {allow_other && <option value="__other__">Other…</option>}
        </select>
        {allow_other && isOther && (
          <input
            className="rf-other-input"
            type="text"
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder="Please specify…"
            required={required}
            autoFocus
          />
        )}
      </label>
    );
  }

  if (type === "checkbox") {
    return (
      <label className="rf-checkfield" htmlFor={id}>
        <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}{required && <span className="rf-req">*</span>}</span>
      </label>
    );
  }

  const inputType = type === "email" ? "email" : type === "number" ? "number" : type === "phone" ? "tel" : "text";
  return (
    <label className="rf-field" htmlFor={id}>
      <span className="rf-label">{label}{required && <span className="rf-req">*</span>}</span>
      <input id={id} type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required} />
    </label>
  );
}

/* ---------- Footer (matches the rest of the site) ---------- */
function Footer() {
  const year = new Date().getFullYear();

  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              width="240"
              height="240"
              className="ft-logo-img"
              style={{ display: "block", objectFit: "contain" }}
            />
          </div>

          <div className="ft-social">
            <a className="ft-social-btn" href="https://www.instagram.com/allstarxperience/#"
               target="_blank" rel="noreferrer" aria-label="Instagram" title="Instagram">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"/>
              </svg>
              <span>Instagram</span>
            </a>
            <a className="ft-social-btn" href="https://www.facebook.com/allstarxperience"
               target="_blank" rel="noreferrer" aria-label="Facebook" title="Facebook">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"/>
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li><span className="ft-k">Email</span><a href="mailto:allstarxperience@gmail.com">allstarxperience@gmail.com</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14387283726">+1 (438) 728-3726</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14385089880">+1 (438) 508-9880</a></li>
            <li><span className="ft-k">City</span><span>Montreal, QC</span></li>
          </ul>
        </div>

        <div className="ft-col explore-col">
          <h4 className="ft-title explore-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>

        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>
          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachiッ.</span>
          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
